import { useCallback, useEffect, useState } from 'react';

const INITIAL = { canScrollLeft: false, canScrollRight: false, viewportWidth: 0 };

/**
 * Horizontal overflow of a scroll container: whether it can scroll further
 * left/right and its visible width. Re-measured on scroll and whenever the
 * container or its first child (e.g. a table) changes size.
 */
const useHorizontalScroll = (ref) => {
  const [state, setState] = useState(INITIAL);

  const update = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    const { scrollLeft, scrollWidth, clientWidth } = element;
    const next = {
      canScrollLeft: scrollLeft > 1,
      canScrollRight: scrollLeft < scrollWidth - clientWidth - 1,
      viewportWidth: clientWidth,
    };
    setState((prev) => (
      prev.canScrollLeft === next.canScrollLeft &&
      prev.canScrollRight === next.canScrollRight &&
      prev.viewportWidth === next.viewportWidth
        ? prev
        : next
    ));
  }, [ref]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    update();
    element.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    return () => {
      element.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [ref, update]);

  /** Scrolls by most of a screen in the given direction (-1 left, 1 right). */
  const scrollByPage = useCallback((direction) => {
    const element = ref.current;
    if (!element) return;
    element.scrollBy({ left: direction * Math.max(240, element.clientWidth * 0.6), behavior: 'smooth' });
  }, [ref]);

  return { ...state, scrollByPage };
};

export default useHorizontalScroll;
